import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { ArrowRight, Building2, Heart, Laptop, GraduationCap, Coffee } from "lucide-react"
import { useMemo } from 'react'

const openings = [
    {
        title: "Senior Investment Analyst",
        department: "Investment",
        location: "New York, NY",
        type: "Full-time",
        description: "Lead investment analysis and due diligence for our venture capital portfolio.",
        requirements: [
            "5+ years of investment analysis experience",
            "Strong financial modeling skills",
            "MBA or equivalent experience"
        ]
    },
    {
        title: "Software Engineer",
        department: "Technology",
        location: "Remote",
        type: "Full-time",
        description: "Build next-generation financial technology solutions using modern tech stack.",
        requirements: [
            "3+ years of full-stack development",
            "Experience with React and TypeScript",
            "Financial sector experience a plus"
        ]
    },
    {
        title: "Research Associate",
        department: "Research",
        location: "London, UK",
        type: "Full-time",
        description: "Conduct research on emerging trends in financial markets and technology.",
        requirements: [
            "Advanced degree in Economics or Finance",
            "Strong analytical and writing skills",
            "Market research experience"
        ]
    },
    {
        title: "Community Manager",
        department: "Community",
        location: "Singapore",
        type: "Full-time",
        description: "Drive engagement and growth of our global community initiatives.",
        requirements: [
            "3+ years community management experience",
            "Strong communication skills",
            "Experience with social impact programs"
        ]
    }
]

const benefits = [
    {
        icon: Laptop,
        title: "Remote-First",
        description: "Work from anywhere with flexible hours"
    },
    {
        icon: GraduationCap,
        title: "Learning & Development",
        description: "Continuous education and skill development"
    },
    {
        icon: Heart,
        title: "Health & Wellness",
        description: "Comprehensive health coverage and wellness programs"
    },
    {
        icon: Coffee,
        title: "Work-Life Balance",
        description: "Generous PTO and flexible scheduling"
    }
]

export function CareersPage() {
    const sortedOpenings = useMemo(() => 
        openings.sort((a, b) => a.department.localeCompare(b.department)),
        [openings]
    )
    
    return (
        <div className="py-12 md:py-24 space-y-12 md:space-y-24">
            {/* Hero Section */}
            <div className="container px-4 mx-auto">
                <div className="max-w-3xl mx-auto text-center">
                    <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-4 md:mb-6">
                        Join Our Team
                    </h1>
                    <p className="text-base md:text-lg text-neutral-600 dark:text-neutral-400">
                        Help us shape the future of finance while building your career
                    </p>
                </div>
            </div>

            {/* Benefits Section */}
            <div className="w-full">
                <Card variant="gradient" size="large" className="w-full rounded-none">
                    <CardContent className="p-6 md:p-10">
                        <div className="max-w-5xl mx-auto text-center space-y-6">
                            <Building2 className="h-10 w-10 md:h-12 md:w-12 mx-auto text-neutral-400" />
                            <h2 className="text-2xl md:text-3xl font-bold">Why Polymath?</h2>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8 mt-8 md:mt-12">
                                {benefits.map((benefit) => {
                                    const Icon = benefit.icon
                                    return (
                                        <div key={benefit.title} className="space-y-3">
                                            <Icon className="h-6 w-6 md:h-8 md:w-8 mx-auto text-neutral-300" />
                                            <h3 className="text-lg md:text-xl font-semibold">{benefit.title}</h3>
                                            <p className="text-sm md:text-base text-neutral-300">{benefit.description}</p>
                                        </div>
                                    )
                                })}
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Open Positions */}
            <div className="container px-4 mx-auto">
                <div className="text-center mb-10 md:mb-16">
                    <h2 className="text-2xl md:text-3xl font-bold mb-3 md:mb-4">Open Positions</h2>
                    <p className="text-base md:text-lg text-neutral-600 dark:text-neutral-400">
                        Find your next opportunity
                    </p>
                </div>
                <div className="space-y-6 md:space-y-8">
                    {sortedOpenings.map((job) => (
                        <Card key={job.title} variant="default">
                            <CardHeader>
                                <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
                                    <div>
                                        <CardTitle className="text-xl mb-1">{job.title}</CardTitle>
                                        <div className="flex flex-wrap gap-2 sm:gap-0 sm:space-x-4 text-sm text-neutral-600 dark:text-neutral-400">
                                            <span>{job.department}</span>
                                            <span className="hidden sm:inline">•</span>
                                            <span>{job.location}</span>
                                            <span className="hidden sm:inline">•</span>
                                            <span>{job.type}</span>
                                        </div>
                                    </div>
                                    <Button className="w-full sm:w-auto">
                                        Apply Now
                                        <ArrowRight className="ml-2 h-4 w-4" />
                                    </Button>
                                </div>
                            </CardHeader>
                            <CardContent>
                                <p className="mb-4 text-neutral-600 dark:text-neutral-400">
                                    {job.description}
                                </p>
                                <div>
                                    <h4 className="font-semibold mb-2">Requirements:</h4>
                                    <ul className="list-disc list-inside space-y-1 text-neutral-600 dark:text-neutral-400">
                                        {job.requirements.map((req) => (
                                            <li key={req}>{req}</li>
                                        ))}
                                    </ul>
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            </div>
        </div>
    )
} 